import React, { useMemo, useState } from 'react';
import type { Edge, Member } from '../../lib/a2aApi';
import './a2a.css';
import { useTranslation } from 'react-i18next';

interface Props {
  members: Member[];
  edges: Edge[];
  /** edges "from>to" that currently carry an active grant / a revoked one */
  liveEdges?: Set<string>;
  deadEdges?: Set<string>;
  selectedEdge?: string | null;
  onSelectEdge?: (key: string | null) => void;
  /** editable: click a node, then another, to declare an allowed delegation */
  onAddEdge?: (from: string, to: string) => void;
  onRemoveEdge?: (key: string) => void;
  nameOf?: (id: string) => string;
}

const W = 168; const H = 46; const GX = 90; const GY = 26; const PAD = 20;

/** Layered layout: a node's column is its longest distance from a source; cycles are tolerated (capped). */
export function layout(members: Member[], edges: Edge[]) {
  const layer = new Map<string, number>(); members.forEach((m) => layer.set(m.agent_id, 0));
  for (let i = 0; i < members.length; i++) for (const e of edges) {
    if (layer.has(e.from) && layer.has(e.to) && (layer.get(e.to) as number) < (layer.get(e.from) as number) + 1 && (layer.get(e.from) as number) < members.length) layer.set(e.to, (layer.get(e.from) as number) + 1);
  }
  const cols = new Map<number, string[]>();
  members.forEach((m) => { const l = layer.get(m.agent_id) || 0; cols.set(l, [...(cols.get(l) || []), m.agent_id]); });
  const pos = new Map<string, { x: number; y: number }>();
  let maxRows = 1;
  cols.forEach((ids, l) => { maxRows = Math.max(maxRows, ids.length); ids.forEach((id, i) => pos.set(id, { x: PAD + l * (W + GX), y: PAD + i * (H + GY) })); });
  const maxCol = Math.max(0, ...Array.from(cols.keys()));
  return { pos, width: PAD * 2 + (maxCol + 1) * W + maxCol * GX, height: PAD * 2 + maxRows * H + (maxRows - 1) * GY };
}

const TopologyGraph: React.FC<Props> = ({ members, edges, liveEdges, deadEdges, selectedEdge, onSelectEdge, onAddEdge, onRemoveEdge, nameOf }) => {
  const { t } = useTranslation();
  const [armed, setArmed] = useState<string | null>(null);
  const { pos, width, height } = useMemo(() => layout(members, edges), [members, edges]);
  const label = (id: string) => (nameOf ? nameOf(id) : id);

  const clickNode = (id: string) => {
    if (!onAddEdge) return;
    if (!armed) return setArmed(id);
    if (armed !== id) onAddEdge(armed, id);
    setArmed(null);
  };

  return (
    <div>
      <svg className="a2a-graph" viewBox={`0 0 ${Math.max(width, 420)} ${Math.max(height, 90)}`} style={{ maxHeight: 360 }} role="img" aria-label={t('components.a2a.topologyGraph.delegationTopology')}>
        <defs>
          <marker id="a2a-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse"><path d="M0,0 L10,5 L0,10 z" fill="currentColor" style={{ color: 'var(--text-3, #888)' }} /></marker>
        </defs>
        {edges.map((e) => {
          const a = pos.get(e.from); const b = pos.get(e.to); if (!a || !b) return null;
          const key = `${e.from}>${e.to}`;
          const x1 = a.x + W; const y1 = a.y + H / 2; const x2 = b.x; const y2 = b.y + H / 2;
          const back = x2 <= x1;
          const path = back ? `M${a.x + W / 2},${a.y + H} C${a.x + W / 2},${a.y + H + 60} ${b.x + W / 2},${b.y + H + 60} ${b.x + W / 2},${b.y + H}` : `M${x1},${y1} C${x1 + GX / 2},${y1} ${x2 - GX / 2},${y2} ${x2 - 2},${y2}`;
          const cls = `a2a-edge ${deadEdges?.has(key) ? 'dead' : liveEdges?.has(key) ? 'live' : ''} ${selectedEdge === key ? 'sel' : ''}`;
          return (
            <g key={key} style={{ cursor: onSelectEdge ? 'pointer' : 'default' }} onClick={() => onSelectEdge?.(selectedEdge === key ? null : key)}>
              <path d={path} className={cls} markerEnd="url(#a2a-arrow)" />
              <path d={path} stroke="transparent" strokeWidth={14} fill="none" />
              <text className="a2a-edge-label" x={back ? a.x + W / 2 : (x1 + x2) / 2} y={back ? a.y + H + 48 : (y1 + y2) / 2 - 6} textAnchor="middle">≤ {e.reveal_max || t('components.a2a.topologyGraph.redacted')}</text>
            </g>
          );
        })}
        {members.map((m) => {
          const p = pos.get(m.agent_id); if (!p) return null;
          return (
            <g key={m.agent_id} className={`a2a-node ${m.role} ${armed === m.agent_id ? 'armed' : ''}`} transform={`translate(${p.x},${p.y})`} onClick={() => clickNode(m.agent_id)} style={{ cursor: onAddEdge ? 'pointer' : 'default' }}>
              <rect width={W} height={H} rx={8} />
              <text x={10} y={19} fontWeight={600}>{label(m.agent_id).slice(0, 22)}</text>
              <text className="sub" x={10} y={35}>{m.role}{m.owner_user_id ? ` · ${m.owner_user_id}` : ''}</text>
            </g>
          );
        })}
      </svg>
      {onAddEdge && (
        <div className="a2a-muted" style={{ marginTop: 6 }}>
          {armed ? <>{t('components.a2a.topologyGraph.nowClickTheAgentThat')} <strong>{label(armed)}</strong> {t('components.a2a.topologyGraph.mayDelegateToClickItAgain')}</> : t('components.a2a.topologyGraph.clickAnAgentThen')}
          {selectedEdge && onRemoveEdge && <button className="ent-btn sm" style={{ marginLeft: 10 }} onClick={() => { onRemoveEdge(selectedEdge); onSelectEdge?.(null); }}>{t('components.a2a.topologyGraph.removeSelectedEdge')}</button>}
        </div>
      )}
    </div>
  );
};

export default TopologyGraph;
